import React, { useState } from 'react';
import axios from 'axios';

/**
 * Admin Fee Slab Configuration - Configure payin & payout slab-based fees per merchant.
 */
const AdminFeeSlabConfig = () => {
  const [merchantId, setMerchantId] = useState('');
  const [payinSlabs, setPayinSlabs] = useState([]);
  const [payoutSlabs, setPayoutSlabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [merchantValid, setMerchantValid] = useState(null); // null=unchecked, true=valid, false=invalid

  const fetchSlabs = async () => {
    if (!merchantId) return;
    setLoading(true);
    setMessage('');
    setMerchantValid(null);
    try {
      // First verify the merchant exists
      try {
        const merchantRes = await axios.get(`/api/admin/merchants/${merchantId}`);
        if (!merchantRes.data.success || !merchantRes.data.data) {
          setMerchantValid(false);
          setPayinSlabs([]);
          setPayoutSlabs([]);
          setMessage(`❌ Merchant #${merchantId} does not exist`);
          setLoading(false);
          return;
        }
      } catch (merr) {
        // Any error from merchant lookup means merchant doesn't exist
        setMerchantValid(false);
        setPayinSlabs([]);
        setPayoutSlabs([]);
        setMessage(`❌ Merchant #${merchantId} does not exist`);
        setLoading(false);
        return;
      }
      setMerchantValid(true);

      const res = await axios.get(`/api/admin/fee-slabs/${merchantId}`);
      if (res.data.success) {
        const all = res.data.data || [];
        const payin = all.filter(s => s.txnType === 'PAYIN');
        const payout = all.filter(s => s.txnType === 'PAYOUT');
        setPayinSlabs(payin);
        setPayoutSlabs(payout);
        if (payin.length === 0 && payout.length === 0) {
          setMessage(`⚠️ No fee slabs configured yet for Merchant #${merchantId}`);
        } else {
          setMessage(`✅ Loaded ${payin.length} payin + ${payout.length} payout slabs for Merchant #${merchantId}`);
        }
      } else {
        setPayinSlabs([]);
        setPayoutSlabs([]);
        setMessage('⚠️ No slabs found for this merchant');
      }
    } catch (e) {
      setPayinSlabs([]);
      setPayoutSlabs([]);
      setMessage('❌ Failed to load fee slabs: ' + (e.response?.data?.message || e.message));
    } finally { setLoading(false); }
  };

  const addSlab = (type) => {
    const newSlab = { minAmount: '', maxAmount: '', feeFlat: '0', feePercent: '0', gstPercent: '18' };
    if (type === 'PAYIN') setPayinSlabs([...payinSlabs, newSlab]);
    else setPayoutSlabs([...payoutSlabs, newSlab]);
  };

  const updateSlab = (type, index, field, value) => {
    // Block negative values for all numeric fields
    if (value !== '' && value.includes('-')) return;
    if (type === 'PAYIN') {
      const updated = [...payinSlabs];
      updated[index] = { ...updated[index], [field]: value };
      setPayinSlabs(updated);
    } else {
      const updated = [...payoutSlabs];
      updated[index] = { ...updated[index], [field]: value };
      setPayoutSlabs(updated);
    }
  };

  const removeSlab = async (type, index) => {
    const slabs = type === 'PAYIN' ? payinSlabs : payoutSlabs;
    const slab = slabs[index];

    // If slab has an ID, it exists in DB — delete from backend
    if (slab.id) {
      try {
        await axios.delete(`/api/admin/fee-slabs/slab/${slab.id}`);
      } catch (e) {
        alert('Failed to delete slab: ' + (e.response?.data?.message || e.message));
        return;
      }
    }

    // Remove from local state
    if (type === 'PAYIN') setPayinSlabs(payinSlabs.filter((_, i) => i !== index));
    else setPayoutSlabs(payoutSlabs.filter((_, i) => i !== index));
    setMessage(`✅ Slab deleted`);
  };

  const saveSlabs = async (type) => {
    const slabs = type === 'PAYIN' ? payinSlabs : payoutSlabs;
    if (!merchantId || merchantId === '0') { alert('Enter a valid Merchant ID first'); return; }
    if (slabs.length === 0) { alert('Add at least one slab'); return; }

    // Validate all slabs have required min/max amounts
    for (let i = 0; i < slabs.length; i++) {
      const s = slabs[i];
      if (s.minAmount === '' || s.minAmount === null || s.minAmount === undefined) {
        alert(`Slab ${i + 1}: Min Amount is required`);
        return;
      }
      if (s.maxAmount === '' || s.maxAmount === null || s.maxAmount === undefined) {
        alert(`Slab ${i + 1}: Max Amount is required`);
        return;
      }
      const min = parseFloat(s.minAmount);
      const max = parseFloat(s.maxAmount);
      if (isNaN(min) || min < 0) {
        alert(`Slab ${i + 1}: Min Amount must be a valid non-negative number`);
        return;
      }
      if (isNaN(max) || max <= 0) {
        alert(`Slab ${i + 1}: Max Amount must be a valid positive number`);
        return;
      }
      if (max <= min) {
        alert(`Slab ${i + 1}: Max Amount (${max}) must be greater than Min Amount (${min})`);
        return;
      }
    }
    
    setLoading(true);
    setMessage('');
    try {
      const res = await axios.post(`/api/admin/fee-slabs/${merchantId}/${type}`, slabs);
      if (res.data.success) {
        setMessage(`✅ ${type} slabs saved successfully!`);
        alert(`${type} slabs saved successfully!`);
        fetchSlabs();
      } else {
        setMessage('❌ Save failed: ' + (res.data.message || 'Unknown error'));
        alert('Save failed: ' + (res.data.message || 'Unknown error'));
      }
    } catch (e) {
      const errMsg = e.response?.data?.message || e.message;
      setMessage('❌ Save failed: ' + errMsg);
      alert('Save failed: ' + errMsg);
    } finally { setLoading(false); }
  };

  const renderSlabTable = (type, slabs) => (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h4 style={{ margin: 0 }}>{type === 'PAYIN' ? '💰 Payin Fee Slabs' : '💸 Payout Fee Slabs'}</h4>
        <div>
          <button onClick={() => { console.log('ADD CLICKED'); addSlab(type); }} style={btnStyle('#388e3c')}>+ Add Slab</button>
          <button onClick={() => { console.log('SAVE CLICKED', type, merchantId); saveSlabs(type); }} style={{ ...btnStyle('#1976d2'), marginLeft: '8px' }}>
            Save {type} Slabs
          </button>
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ background: '#f5f5f5' }}>
            <th style={thStyle}>Min Amount (₹)</th>
            <th style={thStyle}>Max Amount (₹)</th>
            <th style={thStyle}>Fee Flat (₹)</th>
            <th style={thStyle}>Fee % </th>
            <th style={thStyle}>GST %</th>
            <th style={thStyle}>Action</th>
          </tr>
        </thead>
        <tbody>
          {slabs.length === 0 ? (
            <tr><td colSpan={6} style={{ textAlign: 'center', padding: '16px', color: '#888' }}>No slabs configured. Click "+ Add Slab" to start.</td></tr>
          ) : slabs.map((slab, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
              <td style={tdStyle}><input type="number" value={slab.minAmount} onChange={e => updateSlab(type, i, 'minAmount', e.target.value)} style={inputStyle} placeholder="Required" min="0" required /></td>
              <td style={tdStyle}><input type="number" value={slab.maxAmount} onChange={e => updateSlab(type, i, 'maxAmount', e.target.value)} style={inputStyle} placeholder="Required" min="1" required /></td>
              <td style={tdStyle}><input type="number" value={slab.feeFlat} onChange={e => updateSlab(type, i, 'feeFlat', e.target.value)} style={inputStyle} placeholder="0.00" step="0.5" min="0" /></td>
              <td style={tdStyle}><input type="number" value={slab.feePercent} onChange={e => updateSlab(type, i, 'feePercent', e.target.value)} style={inputStyle} placeholder="0" step="0.1" min="0" /></td>
              <td style={tdStyle}><input type="number" value={slab.gstPercent} onChange={e => updateSlab(type, i, 'gstPercent', e.target.value)} style={inputStyle} placeholder="18" min="0" /></td>
              <td style={tdStyle}><button onClick={() => removeSlab(type, i)} style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontSize: '16px' }}>🗑️</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ padding: '20px' }}>
      <h3 style={{ marginBottom: '16px' }}>⚙️ Merchant Fee Slab Configuration</h3>
      <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
        Configure slab-based fees for payins and payouts per merchant. Fee = Flat + (Amount × Percent/100). GST is applied on the fee.
      </p>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
        <label style={{ fontWeight: '600' }}>Merchant ID:</label>
        <input
          type="number"
          value={merchantId}
          onChange={e => { const val = e.target.value; if (val === '' || /^\d+$/.test(val)) { setMerchantId(val); setPayinSlabs([]); setPayoutSlabs([]); setMessage(''); setMerchantValid(null); } }}
          placeholder="Enter merchant ID"
          min="1"
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', width: '150px' }}
        />
        <button onClick={fetchSlabs} disabled={!merchantId || loading} style={btnStyle('#0d6efd')}>
          Load Slabs
        </button>
      </div>

      {message && (
        <div style={{ padding: '8px 12px', marginBottom: '16px', borderRadius: '4px', 
          background: message.includes('❌') || message.includes('Failed') ? '#ffebee' 
            : message.includes('⚠️') ? '#fff8e1'
            : '#e8f5e9',
          color: message.includes('❌') || message.includes('Failed') ? '#c62828' 
            : message.includes('⚠️') ? '#f57f17'
            : '#2e7d32', fontSize: '13px' }}>
          {message}
        </div>
      )}

      {merchantId && merchantValid !== false && (
        <>
          {renderSlabTable('PAYIN', payinSlabs)}
          {renderSlabTable('PAYOUT', payoutSlabs)}
        </>
      )}
    </div>
  );
};

const btnStyle = (bg) => ({
  padding: '6px 14px', background: bg, color: '#fff', border: 'none',
  borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
  opacity: 1, pointerEvents: 'auto'
});
const thStyle = { padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd', fontSize: '12px', fontWeight: '600' };
const tdStyle = { padding: '4px 6px' };
const inputStyle = { width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '3px', fontSize: '13px' };

export default AdminFeeSlabConfig;
